import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { NewsletterSubscribeSection } from "src/components/newsletter-subscribe-section";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { HomeBenefits } from "src/sections/home/home-benefits";
import { HomeGraphs } from "src/sections/home/home-data-analysis";
import { HomeGlucoseChat } from "src/sections/home/home-glucose-chat";
import { HomeMessaging } from "src/sections/home/home-messaging";
import { HomeFaqs } from "src/sections/home/home-faqs";
import { HomeFeatures } from "src/sections/home/home-features";
import { HomeHero } from "src/sections/home/home-hero";
import { HomeOpenSource } from "src/sections/home/home-open-source";
import { HomeRealWorldExample } from "src/sections/home/home-real-world-example";
import { HomeReviews } from "src/sections/home/home-reviews";
import { HomeSolutions } from "src/sections/home/home-solutions";
import { HomeVideo } from "src/sections/home/home-video";

const Page = () => {
  const { t } = useTranslation();
  usePageView();

  const targetRefHomeFeatures = useRef<HTMLDivElement>(null);
  const targetRefHomePricing = useRef<HTMLDivElement>(null);

  const handleScroll = (pagePart: "features" | "pricing") => {
    if (targetRefHomeFeatures.current && pagePart === "features") {
      targetRefHomeFeatures.current.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (targetRefHomePricing.current && pagePart === "pricing") {
      targetRefHomePricing.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Homepage structured data
  const homepageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Opensource.clinic - Nightscout Hosting & Diabetes Management",
    description:
      "Simplified open-source technology for your diabetes management. Join a global movement using transparent, customizable, and affordable solutions to live healthier.",
    url: "https://www.opensource.clinic",
    mainEntity: {
      "@type": "Service",
      serviceType: "Diabetes Management Services",
      provider: {
        "@type": "Organization",
        name: "Opensource.clinic",
      },
      areaServed: "Worldwide",
      description:
        "Nightscout hosting and personalized coaching for diabetes management using open-source looping technology.",
    },
  };

  return (
    <>
      <Seo
        title={t("Nightscout Hosting and Diabetes Coaching")}
        type="website"
        description={t(
          "Simplified open-source technology for your diabetes management. OpenSource.Clinic offers Nightscout hosting with personalized coaching. Join a global movement using transparent, customizable, and affordable solutions to live healthier."
        )}
        keywords="diabetes management, Nightscout hosting, open-source looping, AndroidAPS, diabetes coaching, affordable diabetes care, DIY looping, diabetes technology"
        structuredData={homepageSchema}
      />
      <main>
        <section aria-label="Hero section">
          <HomeHero handleScroll={handleScroll} />
        </section>
        <section aria-label="Solutions">
          <HomeSolutions onViewAll={() => handleScroll("pricing")} />
        </section>
        {/* <section aria-label="Video demonstration">
          <HomeVideo />
        </section> */}
        <section
          aria-label="Benefits of open source"
          ref={targetRefHomeFeatures}
        >
          <HomeBenefits targetRef={targetRefHomeFeatures} />
        </section>
        <section aria-label="AndroidAPS real-world example">
          <HomeRealWorldExample />
        </section>
        <section aria-label="Is open source for you">
          <HomeOpenSource />
        </section>
        <section aria-label="Customer reviews">
          <HomeReviews />
        </section>
        <section aria-label="Platform dashboard">
          <HomeGraphs />
        </section>
        {/* <section aria-label="Glucose Chat">
          <HomeGlucoseChat onScrollToJourney={() => handleScroll("pricing")} />
        </section>
        <section aria-label="Messaging">
          <HomeMessaging onScrollToJourney={() => handleScroll("pricing")} />
        </section> */}
        <section aria-label="Services and pricing" ref={targetRefHomePricing}>
          <HomeFeatures
            targetRef={targetRefHomePricing}
            showNewsletter={false}
          />
        </section>
        <NewsletterSubscribeSection sx={{ bgcolor: "common.white" }} />
        <section aria-label="Frequently asked questions">
          <HomeFaqs />
        </section>
      </main>
    </>
  );
};

export default Page;
